import React, { useEffect } from 'react';
import { Shield, Lock, Eye, FileText, ArrowLeft, Server, Globe } from 'lucide-react';

interface PrivacyPolicyProps {
  onNavigate: (page: string) => void;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onNavigate }) => {
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-stone-50 min-h-screen pt-32 pb-20">
      <div className="max-w-4xl mx-auto px-6">
        
        {/* Navigation */}
        <button 
          onClick={() => onNavigate('home')}
          className="flex items-center gap-2 text-stone-500 hover:text-stone-900 text-xs uppercase tracking-widest mb-8 transition-colors"
        >
          <ArrowLeft size={14} /> Back to Home
        </button>

        {/* Header */}
        <div className="bg-white border border-stone-200 p-10 md:p-16 shadow-sm mb-8 relative overflow-hidden">
           <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-gold-300 via-gold-500 to-gold-300"></div>
           <div className="absolute -right-10 -top-10 text-stone-50 opacity-50 transform rotate-12">
              <Shield size={200} />
           </div>
           
           <div className="relative z-10">
             <span className="text-gold-600 text-xs tracking-[0.3em] uppercase font-bold mb-4 block">Legal & Compliance</span>
             <h1 className="font-serif text-4xl md:text-5xl text-stone-900 mb-6">Privacy Policy</h1>
             <p className="text-stone-500 font-light text-sm">Last Updated: October 24, 2026</p>
           </div>
        </div>

        {/* Content Body */}
        <div className="bg-white border border-stone-200 p-10 md:p-16 shadow-sm space-y-12 text-stone-700 leading-relaxed font-light">
          
          <section>
            <h2 className="font-serif text-2xl text-stone-900 mb-4 flex items-center gap-3">
              <Shield className="text-gold-600" size={20} /> 1. Our Commitment
            </h2>
            <p className="mb-4">
              At Sparkling Gems, we are committed to maintaining the trust and confidence of our visitors to our web site. In this Privacy Policy, we’ve provided detailed information on when and why we collect your personal information, how we use it, the limited conditions under which we may disclose it to others and how we keep it secure.
            </p>
            <p>
              We treat your personal data with the same care and respect as we do the rare gemstones in our vault. Your privacy is a luxury we intend to protect.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-stone-900 mb-4 flex items-center gap-3">
              <Eye className="text-gold-600" size={20} /> 2. Information We Collect
            </h2>
            <p className="mb-4">
              We collect information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4 marker:text-gold-500">
              <li>Register for a Member account.</li>
              <li>Purchase products or services (Shipping address, payment details).</li>
              <li>Contact our Concierge team for bespoke inquiries.</li>
              <li>Subscribe to our newsletter or waitlists.</li>
            </ul>
            <p>
              <strong>Automated Data:</strong> When you visit our site, we automatically collect certain information about your device, including information about your web browser, IP address, time zone, and some of the cookies that are installed on your device.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-stone-900 mb-4 flex items-center gap-3">
              <FileText className="text-gold-600" size={20} /> 3. How We Use Your Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
               <div className="bg-stone-50 p-6 rounded-sm border border-stone-100">
                  <h3 className="font-bold text-stone-900 text-sm uppercase tracking-wide mb-2">Order Fulfillment</h3>
                  <p className="text-xs">To process transactions, arrange for shipping, provide invoices and order confirmations, and handle returns.</p>
               </div>
               <div className="bg-stone-50 p-6 rounded-sm border border-stone-100">
                  <h3 className="font-bold text-stone-900 text-sm uppercase tracking-wide mb-2">Concierge Services</h3>
                  <p className="text-xs">To communicate with you, verify identity for high-value transactions, and provide personalized jewelry recommendations.</p>
               </div>
               <div className="bg-stone-50 p-6 rounded-sm border border-stone-100">
                  <h3 className="font-bold text-stone-900 text-sm uppercase tracking-wide mb-2">Security</h3>
                  <p className="text-xs">To screen our orders for potential risk or fraud and ensure the integrity of our member platform.</p>
               </div>
               <div className="bg-stone-50 p-6 rounded-sm border border-stone-100">
                  <h3 className="font-bold text-stone-900 text-sm uppercase tracking-wide mb-2">Improvement</h3>
                  <p className="text-xs">To analyze site usage and improve our inventory selection and digital experience.</p>
               </div>
            </div>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-stone-900 mb-4 flex items-center gap-3">
              <Lock className="text-gold-600" size={20} /> 4. Data Security
            </h2>
            <p className="mb-4">
              We use industry-standard Secure Socket Layer (SSL) encryption to protect the confidentiality of your personal information. Payment information is handled by PCI-DSS compliant providers and is never stored in plain text on our servers.
            </p>
            <div className="flex items-center gap-2 text-xs text-stone-500 bg-stone-100 p-3 inline-block rounded-sm">
               <Server size={14} /> Data is stored in secure, access-controlled data centers.
            </div>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-stone-900 mb-4 flex items-center gap-3">
              <Globe className="text-gold-600" size={20} /> 5. Third-Party Sharing
            </h2>
            <p>
              We do not sell, trade, or otherwise transfer to outside parties your Personally Identifiable Information. This does not include trusted third parties who assist us in operating our website, conducting our business, or servicing you (e.g., armored courier services), so long as those parties agree to keep this information confidential.
            </p>
          </section>

          <section className="border-t border-stone-200 pt-8 mt-8">
            <h2 className="font-serif text-xl text-stone-900 mb-2">Contact the Data Privacy Officer</h2>
            <p className="mb-4 text-sm">
              If you have questions regarding this privacy policy or wish to exercise your rights to data access or deletion, please contact us.
            </p>
            <div className="flex flex-col gap-1 text-sm font-medium text-stone-800">
               <span>Sparkling Gems Philippines</span>
               <span>Attn: Privacy Officer</span>
               <span>Unit 88, Luxury Lane, Ayala Avenue</span>
               <span>Makati City, 1200</span>
               <a href="mailto:privacy@sparklinggems.ph" className="text-gold-600 hover:underline mt-2">privacy@sparklinggems.ph</a>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;